
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getLocalSubscriptions, saveLocalSubscriptions } from '@/utils/localStorageUtils';
import { useQueryClient } from '@tanstack/react-query';
import { Subscription } from '@/types/subscription';

export function useSubscriptionCancellation() {
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleCancelSubscription = async (subscription: Subscription, onRefresh: () => void) => {
    setCancellingId(subscription.id);
    
    try {
      // Cancel the Stripe subscription first
      const { data, error: stripeError } = await supabase.functions.invoke('cancel-stripe-subscription', {
        body: {
          subscriptionId: subscription.id,
          customerEmail: subscription.email
        }
      });

      if (stripeError) {
        console.error("Error cancelling Stripe subscription:", stripeError);
        throw new Error("Failed to cancel Stripe subscription");
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to cancel subscription");
      }
      
      // Also update local storage to ensure consistency
      const localSubscriptions = getLocalSubscriptions();
      const updatedLocalSubs = localSubscriptions.map(sub => {
        if (sub.id === subscription.id) {
          return { ...sub, active: false, status: 'cancelled' };
        }
        return sub;
      });
      saveLocalSubscriptions(updatedLocalSubs);
      
      // Invalidate all subscription-related queries to force refresh
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      
      const message = data.cancelledStripeSubscriptions > 0 
        ? `Subscription for ${subscription.customerName} has been cancelled in Stripe and our system`
        : `Subscription for ${subscription.customerName} has been cancelled in our system`;
      
      toast.success(message);
      onRefresh();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error("Failed to cancel subscription");
    } finally {
      setCancellingId(null);
    }
  };

  return {
    cancellingId,
    handleCancelSubscription
  };
}
